import {
  useRef, useState, useEffect, useCallback, useMemo,
} from 'react';

import pointer from '@vlabs/shared/assets/images/timeline/pointer.svg';
import cn from 'classnames';

import st from './Timeline.module.sass';
import TimelineTrack from './TimelineTrack';
import type { TimelineProps } from './types';

const MARGIN = 92; // ширина колонки cлева в пикселях
const STEP_IN_PIXELS = 492; // ширина одного временного шага (rulerStep) в пикселях

// Таблица зависимостей длительности → шага (сек)
const RULER_PRESETS = [
  { max: 5, step: 1 },
  { max: 30, step: 5 },
  { max: 60, step: 10 },
  { max: 150, step: 15 },
  { max: 300, step: 30 },
  { max: Infinity, step: 60 },
] as const;

// ограничитель, чтобы не уходить за границы минимум/максимум
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

// форматируем секунды в MM:SS
const formatSecInTime = (sec: number) => {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

function Timeline({
  duration,
  currentTime,
  onChange,
  tracks,
  className,
  isAudioOnly,
}: TimelineProps): React.JSX.Element {
  const railRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(false);

  const { step: rulerStep, pxPerSec } = useMemo(() => {
    const preset = RULER_PRESETS.find((p) => duration <= p.max)!;
    // чтобы шаг занимал около 492 пикселей
    const pxPerSecNew = STEP_IN_PIXELS / preset.step;
    return { step: preset.step, pxPerSec: pxPerSecNew };
  }, [duration]);

  const contentWidth = Math.max(1, Math.ceil(duration * pxPerSec));
  const totalWidth = contentWidth + MARGIN;

  // переводим координату клика в секунды
  const pxToTime = useCallback((clientX: number) => {
    const rail = railRef.current;
    if (!rail || duration <= 0) return 0;

    const rect = rail.getBoundingClientRect();
    const xInside = clientX - rect.left + rail.scrollLeft - MARGIN;
    const x = clamp(xInside, 0, contentWidth);
    return (x / contentWidth) * duration;
  }, [duration, contentWidth]);

  // генерим позиции меток
  const ticks = useMemo(() => {
    const arr: {
      t: number;
      left: number;
      tooCloseToPrev: boolean;
      tooCloseToNext: boolean;
    }[] = [];

    for (let t = 0; t <= duration; t += rulerStep) {
      arr.push({
        t, left: MARGIN + (t / duration) * contentWidth, tooCloseToPrev: false, tooCloseToNext: false,
      });
    }
    if (arr.at(-1)?.t !== duration) {
      arr.push({
        t: duration, left: MARGIN + contentWidth, tooCloseToPrev: false, tooCloseToNext: false,
      });
    }

    // считаем «слиплись ли» текущая метка со следующей
    const minGapPx = (contentWidth / duration) * (rulerStep / 4);

    for (let i = 0; i < arr.length; i += 1) {
      const cur = arr[i];
      const prev = arr[i - 1];
      const next = arr[i + 1];

      cur.tooCloseToPrev = !!prev && Math.abs(prev.left - cur.left) < minGapPx;
      cur.tooCloseToNext = !!next && Math.abs(next.left - cur.left) < minGapPx;
    }

    return arr;
  }, [duration, contentWidth, rulerStep]);

  // поинтер
  const leftPxForPointer = MARGIN + (duration > 0 ? (currentTime / duration) * contentWidth : 0);

  useEffect(() => {
    if (drag) return;
    const rail = railRef.current;
    if (!rail) return;
    const { scrollLeft, clientWidth } = rail;

    const viewportLeft = scrollLeft + MARGIN;
    const viewportRight = scrollLeft + clientWidth - MARGIN;

    if (leftPxForPointer < viewportLeft) {
      // скроллим влево
      rail.scrollTo({ left: Math.max(leftPxForPointer - MARGIN, 0) });
    } else if (leftPxForPointer > viewportRight) {
      // скроллим вправо
      rail.scrollTo({ left: leftPxForPointer - clientWidth + MARGIN });
    }
  }, [leftPxForPointer, drag]);

  return (
    <div className={cn(st.Root, className)}>
      <div
        aria-label="Timeline"
        aria-valuemax={duration}
        aria-valuemin={0}
        aria-valuenow={currentTime}
        className={st.Rail}
        onClick={(e) => onChange(pxToTime(e.clientX))}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 1 : 0.1;
          if (e.key === 'ArrowLeft') onChange(clamp(currentTime - step, 0, duration));
          if (e.key === 'ArrowRight') onChange(clamp(currentTime + step, 0, duration));
        }}
        onPointerCancel={() => setDrag(false)}
        onPointerDown={(e) => {
          (e.currentTarget as HTMLElement).setPointerCapture?.((e as any).pointerId ?? 0);
          setDrag(true);
          onChange(pxToTime(e.clientX));
        }}
        onPointerMove={(e) => {
          if (drag) onChange(pxToTime(e.clientX));
        }}
        onPointerUp={() => setDrag(false)}
        ref={railRef}
        role="slider"
        tabIndex={0}
      >
        <div
          className={st.Inner}
          style={{ width: totalWidth }}
        >
          <div className={st.Ruler}>
            {ticks.map(({
              t, left, tooCloseToPrev, tooCloseToNext,
            }) => (
              <div
                className={st.Tick}
                key={t}
                style={{ left }}
              >
                <div className={st.TickMark} />
                {!tooCloseToPrev && (
                  <div
                    className={cn(
                      st.TickLabel,
                      tooCloseToNext && st.TickLabel_left,
                    )}
                  >
                    {formatSecInTime(t)}
                  </div>
                )}
              </div>
            ))}
          </div>
          {!!isAudioOnly && <div className={st.AudioOnlySegment} />}
          <div className={st.Tracks}>
            {tracks.map((t, i) => (
              <TimelineTrack
                key={`track-${i + 1}`}
                segments={t.segments}
                title={t.title}
              />
            ))}
          </div>
          <div
            className={cn(st.Pointer, drag && st.Pointer_drag)}
            style={{ left: leftPxForPointer }}
          >
            <img
              alt="pointer"
              draggable={false}
              src={pointer}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Timeline;
